import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Plane, ShieldCheck, CalendarCheck, Gem, Lock, Scale, ArrowUpRight } from "lucide-react";

const SECTIONS = [
  {
    icon: Gem,
    title: "One Standard. Every Guest.",
    body:
      "Rokert Retreats exists for a single promise — an experience so complete it cannot be matched, delivered identically to every guest we welcome. We do not sell rooms; we compose journeys. Whether you arrive in the height of the season or on a quiet week in between, the depth, care and refinement of your retreat remain exactly the same.",
  },
  {
    icon: ShieldCheck,
    title: "A Curated Collection, Not a Single Address",
    body:
      "Your stay is drawn from a private pool of properties, each personally vetted and held to one uncompromising benchmark of quality, comfort and discretion. Because your journey is sourced from this collection rather than a single villa, availability is assured and your comfort is never left to chance. If a preferred property is unavailable, another of equal — or greater — standard takes its place, seamlessly and without compromise.",
  },
  {
    icon: CalendarCheck,
    title: "Your Arrival Date Is Never a Constraint",
    body:
      "Choose any arrival date that suits you. When you design your journey, your chosen dates will always be honoured — availability is guaranteed by the breadth of our collection. There is no waiting list, no 'sold out', and no compromise on the days you have set aside for yourself. Simply select your arrival and let us take care of the rest.",
  },
  {
    icon: Plane,
    title: "Flights & Travel Arrangements",
    body:
      "Guests arrange their own international and domestic flights, giving you complete freedom over airline, cabin and schedule. From the moment you land in Sri Lanka, every detail is ours: private transfers, arrival rituals, and a concierge attending to your journey end to end. Simply share your arrival details and we will be there.",
  },
  {
    icon: Scale,
    title: "Reservations, Deposits & Changes",
    body:
      "A journey is confirmed with a reservation deposit, with the balance settled ahead of arrival. Dates and details may be adjusted in consultation with your concierge subject to notice. Full terms of deposit, rescheduling and cancellation are shared in your personal itinerary and confirmed in writing before any commitment.",
  },
  {
    icon: Lock,
    title: "Privacy & Discretion",
    body:
      "Discretion is the foundation of everything we do. Guest identities, itineraries and preferences are held in strict confidence and shared only with the small circle of people entrusted with your care. Your journey is yours alone.",
  },
];

export default function Policy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-rr-bg text-rr-ivory min-h-screen" data-testid="policy-page">
      {/* Hero */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(120% 90% at 50% 0%, rgba(201,164,107,0.10) 0%, transparent 55%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-14 text-center fade-up">
          <img src="/media/rokert-emblem.png" alt="Rokert Retreats emblem" className="h-16 w-auto mx-auto mb-8 opacity-90" />
          <span className="overline">The Rokert Promise</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">
            An Experience Beyond<br /><span className="text-rr-gold">Comparison</span>
          </h1>
          <p className="text-rr-muted text-base md:text-lg max-w-2xl mx-auto mt-6 leading-relaxed">
            How we protect the quality, availability and privacy of every journey we design —
            so the only thing you need to decide is when to arrive.
          </p>
          <div className="hairline mx-auto mt-10" />
        </div>
      </section>

      {/* Sections */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-8">
        <div className="space-y-px rounded-2xl overflow-hidden border border-rr-line">
          {SECTIONS.map((s, i) => {
            const Icon = s.icon;
            return (
              <article key={i} data-testid={`policy-section-${i}`}
                className="bg-rr-surface/60 px-6 md:px-10 py-9 flex gap-5 md:gap-7 hover:bg-rr-surface transition-colors">
                <div className="shrink-0">
                  <span className="flex items-center justify-center w-12 h-12 rounded-full bg-rr-gold/12 border border-rr-gold/25">
                    <Icon className="w-5 h-5 text-rr-gold" strokeWidth={1.5} />
                  </span>
                </div>
                <div>
                  <h2 className="font-heading text-2xl md:text-[28px] text-rr-ivory leading-snug">{s.title}</h2>
                  <p className="text-rr-muted text-sm md:text-base leading-relaxed mt-3">{s.body}</p>
                </div>
              </article>
            );
          })}
        </div>

        <p className="text-center text-xs text-rr-muted/70 mt-8 max-w-2xl mx-auto leading-relaxed">
          This page is a summary of the principles that guide every Rokert journey. Full contractual terms,
          including deposit, rescheduling and cancellation conditions, are provided within your personal
          itinerary and confirmed in writing prior to booking.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-16 text-center">
        <h3 className="font-heading text-3xl md:text-4xl text-rr-ivory">Ready to design your journey?</h3>
        <p className="text-rr-muted mt-3">Choose your sanctuary and your dates — we assure the rest.</p>
        <Link to="/retreats" data-testid="policy-cta" className="btn-gold inline-flex items-center gap-2 px-9 py-3.5 mt-7 text-xs uppercase rounded-full">
          Explore the Collection <ArrowUpRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
